import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MoreHorizontal, Ban, Flag, UserMinus, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { invalidateFollowStatus } from "@/hooks/useFollowStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { key: "spam", label: "Spam ou perfil comercial abusivo" },
  { key: "fake", label: "Perfil falso / se passa por outra pessoa" },
  { key: "harassment", label: "Assédio ou bullying" },
  { key: "offensive", label: "Conteúdo ofensivo" },
  { key: "fraud", label: "Golpe ou fraude" },
  { key: "other", label: "Outro motivo" },
];

/** ⋮ menu on another user's profile: block/unblock, remove follower, report. */
export default function UserActionsMenu({ userId, userName, relationship, onChanged }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const blocked = !!relationship?.blocked_by_me;

  const toggleBlock = async () => {
    if (!blocked && !window.confirm(`Bloquear ${userName}? Vocês deixarão de se seguir e não poderão interagir.`)) return;
    setBusy(true);
    try {
      if (blocked) await axios.delete(`${API}/users/${userId}/block`);
      else await axios.post(`${API}/users/${userId}/block`);
      invalidateFollowStatus?.(userId);
      toast.success(blocked ? "Usuário desbloqueado" : "Usuário bloqueado");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const removeFollower = async () => {
    if (!window.confirm(`Remover ${userName} dos seus seguidores?`)) return;
    setBusy(true);
    try { await axios.delete(`${API}/users/${userId}/follower`); toast.success("Seguidor removido"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const sendReport = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/users/${userId}/report`, { reason, details: details.trim() || undefined });
      toast.success("Denúncia enviada. Obrigado por ajudar a manter a comunidade segura.");
      setReportOpen(false); setDetails("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors border border-border" aria-label="Mais opções" data-testid="user-actions-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-5 h-5" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid="user-actions-menu">
          {relationship?.follows_me && (
            <DropdownMenuItem onClick={removeFollower} data-testid="user-action-remove-follower"><UserMinus className="w-4 h-4 mr-2" /> Remover dos seguidores</DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={toggleBlock} data-testid="user-action-block"><Ban className="w-4 h-4 mr-2" /> {blocked ? "Desbloquear" : "Bloquear"}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setReportOpen(true)} className="text-destructive" data-testid="user-action-report"><Flag className="w-4 h-4 mr-2" /> Denunciar usuário</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-user-dialog">
          <DialogHeader><DialogTitle>Denunciar {userName}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {REASONS.map((r) => (
              <label key={r.key} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${reason === r.key ? "border-primary bg-primary/5" : "border-border"}`}>
                <input type="radio" name="user-report-reason" value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)} data-testid={`report-user-reason-${r.key}`} />
                {r.label}
              </label>
            ))}
            <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Detalhes (opcional)" data-testid="report-user-details" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cancelar</Button>
            <Button onClick={sendReport} disabled={busy} className="bg-destructive hover:bg-destructive/90" data-testid="report-user-submit">Enviar denúncia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
